import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { Item } from "../../pojo/Item";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { Rules } from "../s25-rule-tree/s25.rule.const";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25Util } from "../../util/s25-util";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { S25TimepickerModule } from "../s25-timepicker/s25.timepicker.module";
import { S25ToggleButtonModule } from "../s25-toggle-button/s25.toggle.button.module";

@TypeManagerDecorator("s25-ng-custom-attribute-value")
@Component({
    selector: "s25-ng-custom-attribute-value",
    template: `
        <ng-container *ngIf="isInit">
            <div class="attributeRow">
                <label [for]="'customAttributeOperator' + currentCount">Operator: </label>
                <select
                    [ngModel]="operator"
                    (ngModelChange)="onOperatorChange($event)"
                    class="cn-form__control"
                    [id]="'customAttributeOperator' + currentCount"
                    name="operator"
                >
                    <option *ngFor="let operator of availableOperators" [ngValue]="operator">
                        {{ operator }}
                    </option>
                </select>
            </div>

            @if (!Rules.valuelessOperators.has(operator)) {
                <div class="attributeRow" [ngSwitch]="attributeType">
                    <label [for]="'customAttributeValue' + currentCount">Value: </label>

                    <s25-toggle-button
                        *ngSwitchCase="AttributeType.Boolean"
                        [value]="value"
                        (valueChange)="onValueChange($event)"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    ></s25-toggle-button>

                    <ng-container *ngSwitchCase="AttributeType.Text">
                        @if (!discreteOptions[attributeId] || operator === "contains" || operator === "not contains") {
                            <input
                                class="c-input"
                                type="text"
                                [maxLength]="80"
                                [ngModel]="value"
                                (ngModelChange)="onValueChange($event)"
                                [id]="'customAttributeValue' + currentCount"
                                name="attribute value"
                            />
                        } @else if (operator === "in" || operator === "not in") {
                            <s25-ng-generic-multiselect-dropdown
                                [value]="multiValue"
                                [items]="discreteOptions[attributeId]"
                                (done)="onMultiChange($event)"
                            />
                        } @else {
                            <select
                                [ngModel]="value"
                                (ngModelChange)="onValueChange($event)"
                                class="c-input"
                                [id]="'customAttributeValue' + currentCount"
                                name="attribute value"
                            >
                                <option value="" disabled selected>Select a value</option>
                                <option *ngFor="let opt of discreteOptions[attributeId]" [ngValue]="opt.itemId">
                                    {{ opt.itemName }}
                                </option>
                            </select>
                        }
                    </ng-container>

                    <input
                        *ngSwitchCase="AttributeType.Integer"
                        class="c-input"
                        type="number"
                        step="1"
                        [ngModel]="value"
                        (ngModelChange)="onValueChange($event)"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    />

                    <input
                        *ngSwitchCase="AttributeType.Float"
                        class="c-input"
                        type="number"
                        [ngModel]="value"
                        (ngModelChange)="onValueChange($event)"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    />

                    <textarea
                        *ngSwitchCase="AttributeType.LongText"
                        class="c-input"
                        [maxLength]="10000"
                        [ngModel]="value"
                        (ngModelChange)="onValueChange($event)"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    ></textarea>

                    <input
                        *ngSwitchCase="AttributeType.URL"
                        class="c-input"
                        type="text"
                        [maxLength]="250"
                        [ngModel]="value"
                        (ngModelChange)="onValueChange($event)"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    />

                    <s25-ng-editable-date
                        *ngSwitchCase="AttributeType.Date"
                        [val]="value"
                        (valChange)="onValueChange($event.toISOString())"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    ></s25-ng-editable-date>

                    <s25-ng-editable-date-time
                        *ngSwitchCase="AttributeType.DateTime"
                        [val]="value"
                        (valChange)="onValueChange($event)"
                        [outputString]="true"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    ></s25-ng-editable-date-time>

                    <s25-timepicker
                        *ngSwitchCase="AttributeType.Time"
                        [modelValue]="value"
                        (modelValueChange)="onValueChange($event.toISOString())"
                        [defaultNow]="true"
                        [id]="'customAttributeValue' + currentCount"
                        name="attribute value"
                    ></s25-timepicker>

                    <ng-container *ngSwitchCase="AttributeType.Location">
                        <ng-container *ngTemplateOutlet="multiselect"></ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="AttributeType.Resource">
                        <ng-container *ngTemplateOutlet="multiselect"></ng-container>
                    </ng-container>

                    <ng-container *ngSwitchCase="AttributeType.Organization">
                        <ng-container *ngTemplateOutlet="multiselect"></ng-container>
                    </ng-container>
                </div>
            }

            <ng-template #multiselect>
                <s25-ng-dropdown-search-criteria
                    [type]="searchCriteria[$any(attributeType)]"
                    [value]="value"
                    (valueChange)="onValueChange($any($event))"
                    [id]="'customAttributeValue' + currentCount"
                    name="attribute value"
                />
            </ng-template>
        </ng-container>
    `,
    styles: `
        .attributeRow {
            display: flex;
            gap: 1em;
            align-items: center;
            margin: 0.5em 0;
        }

        .attributeRow > label:first-child {
            min-width: 10em;
            margin: 0;
        }

        .attributeRow > input {
            min-width: 15em;
        }

        .attributeRow > select {
            min-width: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgSwitch,
        S25ToggleButtonModule,
        FormsModule,
        NgIf,
        NgForOf,
        S25DropdownPaginatedModule,
        NgSwitchCase,
        S25TimepickerModule,
        NgTemplateOutlet,
        S25EditableModule,
    ],
})
export class S25CustomAttributeValueComponent implements OnInit {
    @Input() attributeId: number;
    @Input() attributeType: CustomAttributes.CustAttrCode;
    @Input() operator?: Rules.Operator;
    @Input() value?: CustomAttributes.ConditionValue;

    @Output() operatorChange = new EventEmitter<Rules.Operator>();
    @Output() valueChange = new EventEmitter<CustomAttributes.ConditionValue>();

    AttributeType = CustomAttributes.type;

    isInit = false;
    discreteOptions: Record<number, DropDownItem[]> = {};
    availableOperators: Rules.Operator[] = [];
    searchCriteria = {
        [Item.Ids.Location]: "locations",
        [Item.Ids.Resource]: "resources",
        [Item.Ids.Organization]: "organizations",
    };
    public static count = 0;
    currentCount: number;
    protected readonly Rules = Rules;
    multiValue: DropDownItem[] = [];

    constructor(private changeDetector: ChangeDetectorRef) {
        this.currentCount = S25CustomAttributeValueComponent.count;
        S25CustomAttributeValueComponent.count++;
    }

    async ngOnInit() {
        await this.setDiscreteOptions();
        this.setAvailableOperators();
        if (!this.operator) this.setDefaultOperator();

        if (!!this.discreteOptions[this.attributeId]) {
            this.multiValue = (S25Util.parseJson<string[]>(this.value as string)[0] ?? []).map((id) => ({
                itemId: id,
                itemName: id,
            }));
        }

        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    async setDiscreteOptions() {
        const discreteOptions = await CustomAttributeService.getAllDiscreteOptions();
        for (const option of discreteOptions) {
            this.discreteOptions[option.itemId] = option.opt.map((opt) => ({ itemId: opt, itemName: opt }));
        }
    }

    setAvailableOperators() {
        switch (this.attributeType) {
            case CustomAttributes.type.DateTime:
            case CustomAttributes.type.Date:
            case CustomAttributes.type.Time:
            case CustomAttributes.type.Float:
            case CustomAttributes.type.Integer:
                this.availableOperators = ["=", "≠", "<", "<=", ">=", ">"];
                break;
            case CustomAttributes.type.Boolean:
            case CustomAttributes.type.Organization:
            case CustomAttributes.type.Location:
            case CustomAttributes.type.Resource:
                this.availableOperators = ["=", "≠"];
                break;
            case CustomAttributes.type.Text:
            case CustomAttributes.type.LongText:
            case CustomAttributes.type.URL:
                this.availableOperators = ["=", "≠", "contains", "not contains"];
                break;
        }

        if (!!this.discreteOptions[this.attributeId]) {
            this.availableOperators.push("in", "not in");
        }

        this.availableOperators.push("present", "not present");
    }

    setDefaultOperator() {
        this.onOperatorChange(this.availableOperators[0]);
    }

    onOperatorChange(operator: Rules.Operator) {
        this.operator = operator;
        this.operatorChange.emit(operator);
        this.changeDetector.detectChanges();
    }

    onValueChange(value: CustomAttributes.ConditionValue) {
        this.value = value;
        this.valueChange.emit(value);
        this.changeDetector.detectChanges();
    }

    onMultiChange(value: DropDownItem[]) {
        this.onValueChange(S25Util.stringify(value.map((item) => item.itemId)));
    }
}
